/**
 * useAiExtract Hook
 *
 * Orchestrates AI event extraction from text or image:
 * - Single event → initializeForm() + onSingleExtracted()
 * - Multiple events → sequential draft saves with progress + onMultipleExtracted(count)
 */

import { useState, useCallback } from 'react';
import { useApi, useUser, useTranslations } from 'react-ui';
import { getDraftService } from '../services/draftService';
import { extractEventsFromText, extractEventsFromImage, AiExtractError } from '../services/aiService';
import { aiExtractedToFormData } from '../transformers/aiTransformers';
import type { AiExtractedEventData, EventFormData } from '../types';

export interface UseAiExtractCallbacks {
  /** Called after a single event is extracted and the form is pre-filled */
  onSingleExtracted: (formData: EventFormData, lowConfidence: boolean) => void;
  /** Called after multiple events are saved as drafts. Receives count, titles, and first saved draft ID. */
  onMultipleExtracted: (count: number, titles: string[], firstDraftId: string | undefined) => void;
}

export interface UseAiExtractReturn {
  isExtracting: boolean;
  isSavingDrafts: boolean;
  savedDraftCount: number;
  totalDraftCount: number;
  extractionError: string | null;
  extractFromText: (text: string, locale?: string) => Promise<void>;
  extractFromImage: (file: File, locale?: string) => Promise<void>;
  clearError: () => void;
}

export function useAiExtract(callbacks: UseAiExtractCallbacks): UseAiExtractReturn {
  const [isExtracting, setIsExtracting] = useState(false);
  const [isSavingDrafts, setIsSavingDrafts] = useState(false);
  const [savedDraftCount, setSavedDraftCount] = useState(0);
  const [totalDraftCount, setTotalDraftCount] = useState(0);
  const [extractionError, setExtractionError] = useState<string | null>(null);

  const { apiEndpoints, apiKey } = useApi();
  const { user } = useUser();
  const { translated } = useTranslations();

  const getErrorMessage = useCallback((err: unknown): string => {
    if (err instanceof AiExtractError) {
      if (err.status >= 500) return translated('sc_ai_error_server');
      if (err.status >= 400) return translated('sc_ai_error_client');
    }
    return translated('sc_ai_error_client');
  }, [translated]);

  const clearError = useCallback(() => setExtractionError(null), []);

  const getApiEndpoint = useCallback((): string => {
    if (!apiEndpoints?.metadata) throw new Error('API endpoint not configured');
    return new URL(apiEndpoints.metadata).origin;
  }, [apiEndpoints]);

  const handleExtractionResult = useCallback(
    async (events: AiExtractedEventData[]) => {
      if (events.length === 0) {
        setExtractionError(translated('sc_ai_error_no_events'));
        return;
      }

      if (events.length === 1) {
        const formData = aiExtractedToFormData(events[0]);
        const lowConfidence = events[0].confidence < 0.6;
        callbacks.onSingleExtracted(formData, lowConfidence);
        return;
      }

      // Multiple events — save all as drafts
      if (!user?.token || !apiKey) {
        setExtractionError(translated('sc_ai_error_drafts_login'));
        return;
      }

      const apiEndpoint = getApiEndpoint();
      const draftService = getDraftService(apiEndpoint, apiKey);

      setIsSavingDrafts(true);
      setSavedDraftCount(0);
      setTotalDraftCount(events.length);

      let savedCount = 0;
      let firstDraftId: string | undefined;
      const failedTitles: string[] = [];

      for (const event of events) {
        try {
          const formData = aiExtractedToFormData(event);
          const result = await draftService.createDraft(user.token, formData);
          if (savedCount === 0) firstDraftId = result.draftId;
          savedCount++;
          setSavedDraftCount(savedCount);
        } catch (err) {
          console.error('[useAiExtract] Failed to save draft:', err);
          failedTitles.push(event.title || 'Untitled');
        }
      }

      setIsSavingDrafts(false);

      if (savedCount === 0) {
        setExtractionError(translated('sc_ai_error_drafts_failed'));
        return;
      }

      if (failedTitles.length > 0) {
        console.warn('[useAiExtract] Some drafts failed to save:', failedTitles);
      }

      const savedTitles = events.slice(0, savedCount).map(e => e.title ?? '');
      callbacks.onMultipleExtracted(savedCount, savedTitles, firstDraftId);
    },
    [user, apiKey, getApiEndpoint, callbacks, translated]
  );

  const extractFromText = useCallback(
    async (text: string, locale = 'en') => {
      if (!user?.token || !apiKey) {
        setExtractionError(translated('sc_ai_error_not_logged_in'));
        return;
      }

      setIsExtracting(true);
      setExtractionError(null);

      try {
        const apiEndpoint = getApiEndpoint();
        const response = await extractEventsFromText(
          { apiEndpoint, apiKey, token: user.token },
          text,
          locale
        );
        await handleExtractionResult(response.events ?? []);
      } catch (err) {
        setExtractionError(getErrorMessage(err));
      } finally {
        setIsExtracting(false);
      }
    },
    [user, apiKey, getApiEndpoint, handleExtractionResult, translated, getErrorMessage]
  );

  const extractFromImage = useCallback(
    async (file: File, locale = 'en') => {
      if (!user?.token || !apiKey) {
        setExtractionError(translated('sc_ai_error_not_logged_in'));
        return;
      }

      setIsExtracting(true);
      setExtractionError(null);

      try {
        const apiEndpoint = getApiEndpoint();
        const response = await extractEventsFromImage(
          { apiEndpoint, apiKey, token: user.token },
          file,
          locale
        );
        await handleExtractionResult(response.events ?? []);
      } catch (err) {
        setExtractionError(getErrorMessage(err));
      } finally {
        setIsExtracting(false);
      }
    },
    [user, apiKey, getApiEndpoint, handleExtractionResult, translated, getErrorMessage]
  );

  return {
    isExtracting,
    isSavingDrafts,
    savedDraftCount,
    totalDraftCount,
    extractionError,
    extractFromText,
    extractFromImage,
    clearError,
  };
}
